import { useTranslation } from "@/hooks/useTranslation";
import {
  getPromptStyle,
  getPromptTitle,
  getSuggestionCardAnimationStyle,
  getSupportedLang,
} from "@/lib/prompt-utils";
import { cn } from "@/lib/utils";
import { PromptDTO } from "@shaarp/common";
import { Pin, PinOff } from "lucide-react";
import { useState } from "react";
import { ChatSuggestionCard } from "./chat-suggestion-card";

type PromptSuggestionCardProps = {
  prompt: PromptDTO;
  index: number;
  isPinned: boolean;
  isTransitioning: boolean;
  disabled: boolean;
  onClick: (prompt: PromptDTO) => void;
  onUnpin?: (prompt: PromptDTO) => void;
};

/**
 * Marker on a pinned tactic, doubling as the way to unpin it: it turns red
 * under the pointer to announce that a click removes the pin rather than
 * opening the tactic underneath.
 */
function UnpinBadge({ onUnpin }: { onUnpin: () => void }) {
  const { t } = useTranslation();
  const [isHovering, setIsHovering] = useState(false);

  // Kept from reaching the card, which would send the tactic instead.
  const handleUnpin = (e: React.MouseEvent | React.KeyboardEvent) => {
    e.stopPropagation();
    e.preventDefault();
    onUnpin();
  };

  return (
    <div
      className={cn(
        "absolute -top-1.5 -right-1.5 rounded-full p-1 shadow-sm border-2 border-background z-10 cursor-pointer transition-colors duration-200",
        // A 44px target for a finger, centered on the 22px badge, so a tap
        // meant for it does not send the tactic underneath.
        "touch:after:absolute touch:after:-inset-[11px]",
        isHovering ? "bg-red-500" : "bg-blue-500",
      )}
      onMouseEnter={() => setIsHovering(true)}
      onMouseLeave={() => setIsHovering(false)}
      onClick={handleUnpin}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          handleUnpin(e);
        }
      }}
      aria-label={t("prompt.card.actions.unpin")}
    >
      {isHovering ? (
        <PinOff className="w-2.5 h-2.5 text-white" />
      ) : (
        <Pin className="w-2.5 h-2.5 text-white fill-white" />
      )}
    </div>
  );
}

export function PromptSuggestionCard({
  prompt,
  index,
  isPinned,
  isTransitioning,
  disabled,
  onClick,
  onUnpin,
}: PromptSuggestionCardProps) {
  const { i18n } = useTranslation();
  const lang = getSupportedLang(i18n.language);
  const { Icon, colors } = getPromptStyle(prompt);
  const description = getPromptTitle(prompt, lang);
  const isDisabled = isTransitioning || disabled;

  return (
    <ChatSuggestionCard
      Icon={Icon}
      label={description}
      colors={colors}
      disabled={isDisabled}
      onClick={() => onClick(prompt)}
      style={getSuggestionCardAnimationStyle(index, isTransitioning)}
      badge={isPinned && <UnpinBadge onUnpin={() => onUnpin?.(prompt)} />}
    />
  );
}
